import axios from 'axios';

import { deleteAuthHeader, setAuthHeader } from '@fora-monorepo/webapp-auth';

import { envConfig } from '../../config';
import { logOut } from '../../redux/auth/authSlice';
import { AppDispatch } from '../../redux/store';

let isRefreshingAccessToken = false;
let queuedRequestsToRetry: { resolve: (value: unknown) => void; reject: (reason?: any) => void }[] = [];

export const setRefreshInterceptor = (dispatch: AppDispatch) => {
  axios.interceptors.response.use(
    response => {
      return response;
    },
    async err => {
      const originalConfig = err.config;

      // Auth token was expired, try refreshing token if
      // - Response of request was 401
      // - Request was not made by this interceptor
      // - Request was not a refresh token request (we already know this will fail in that case)
      if (
        err.response?.status === 401 &&
        !originalConfig._retry &&
        !(originalConfig.url.includes('/token') && originalConfig.url.includes('grant_type=refresh_token'))
      ) {
        originalConfig._retry = true;

        // If a refresh token request is already in progress, queue the request
        if (isRefreshingAccessToken) {
          return new Promise((resolve, reject) => {
            queuedRequestsToRetry.push({ resolve, reject });
          })
            .then(token => {
              originalConfig.headers['Authorization'] = `Bearer ${token}`;
              return axios(originalConfig);
            })
            .catch(err => Promise.reject(err));
        }

        // Start the refresh token process
        isRefreshingAccessToken = true;

        return new Promise((resolve, reject) => {
          axios
            .get(`${envConfig.authApiBaseUri}/public/token?authDomain=ORGANIZATION_SAPEP&grant_type=refresh_token`, {
              ...originalConfig,
              withCredentials: true,
              headers: {
                Authorization: undefined,
                'x-selected-partner-number-organization': sessionStorage.getItem('selectedPartnerNumberOrganization'),
              },
            })
            .then(res => {
              if (res.status === 200 && res.data.accessToken) {
                // Set new accessToken as default Authorization header
                setAuthHeader(res.data.accessToken);
                // Use new accessToken when retrying request
                originalConfig.headers.Authorization = `Bearer ${res.data.accessToken}`;

                // Continue all queued requests with the new token
                queuedRequestsToRetry.forEach(({ resolve }) => resolve(res.data.accessToken));
                queuedRequestsToRetry = [];

                // Don't retry request if it was a token request,
                // since that would fail and create an infinite loop
                if (!originalConfig.url.includes('/token')) {
                  resolve(axios(originalConfig));
                }
              } else {
                // If refresh fails, reject all queued requests
                queuedRequestsToRetry.forEach(({ reject }) => reject(err));
                queuedRequestsToRetry = [];
                reject(err);
              }
            })
            .catch(refreshError => {
              deleteAuthHeader();

              // Reject all queued requests
              queuedRequestsToRetry.forEach(({ reject }) => reject(refreshError));
              queuedRequestsToRetry = [];

              reject(refreshError);
            })
            .finally(() => {
              isRefreshingAccessToken = false;
            });
        });
      }

      // If a refresh token request gets 401, we log the user out
      if (err.response.status === 401 && originalConfig.url.includes('grant_type=refresh_token')) {
        dispatch(logOut());
      }

      return Promise.reject(err);
    }
  );
};
